import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { GlassPanel } from "./GlassPanel";
import { api } from "../lib/api";
import {
  computeInstallerHours,
  startOfMondayWeek,
  endOfMondayWeek,
  weeklyCapacityFromHours,
  DEFAULT_WORKDAY_HOURS,
  WEEKLY_CAPACITY_HOURS,
} from "../lib/workload";

/**
 * Admin/office-only pinned alert on the Dashboard that surfaces any installer
 * whose booked hours for the COMING week (next Mon-Sun) meet or exceed 90%
 * of their capacity. Renders nothing when there are no overloads so it stays
 * out of the way on calm weeks. Capacity comes from the per-installer
 * `capacity_hours_per_week` override, falling back to the shop-wide
 * workday_hours total.
 */
export default function OverloadAlerts() {
  const [installers, setInstallers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [workdayHours, setWorkdayHours] = useState(DEFAULT_WORKDAY_HOURS);

  useEffect(() => {
    api.get("/users/roster").then(({ data }) => {
      setInstallers(data);
    }).catch(() => {});
    api.get("/jobs", { params: { main_status: "job" } }).then(({ data }) => setJobs(data)).catch(() => {});
    api.get("/settings/workday-hours").then(({ data }) => setWorkdayHours(data)).catch(() => {});
  }, []);

  // NEXT calendar week (not this one) — the whole point of the alert is
  // proactive: give admins a chance to redistribute before the crew show up
  // Monday morning to an impossible schedule.
  const nextWeek = useMemo(() => {
    const today = new Date();
    const thisMonday = startOfMondayWeek(today);
    const nextMonday = new Date(thisMonday);
    nextMonday.setDate(thisMonday.getDate() + 7);
    return { start: nextMonday, end: endOfMondayWeek(nextMonday) };
  }, []);

  const shopCap = useMemo(() => {
    const t = weeklyCapacityFromHours(workdayHours);
    return t > 0 ? t : WEEKLY_CAPACITY_HOURS;
  }, [workdayHours]);

  const alerts = useMemo(() => {
    const out = [];
    for (const u of installers) {
      const hours = computeInstallerHours(u.id, jobs, nextWeek.start, nextWeek.end, workdayHours);
      const perCap = (u.capacity_hours_per_week === null || u.capacity_hours_per_week === undefined)
        ? null
        : Number(u.capacity_hours_per_week);
      const cap = Number.isFinite(perCap) && perCap > 0 ? perCap : shopCap;
      const pct = cap > 0 ? (hours / cap) * 100 : 0;
      if (pct >= 90) out.push({ user: u, hours, cap, pct });
    }
    // Worst offender first
    return out.sort((a, b) => b.pct - a.pct);
  }, [installers, jobs, workdayHours, nextWeek, shopCap]);

  if (alerts.length === 0) return null;

  const fmtYmd = (d) => {
    // Format from LOCAL year/month/day — toISOString() converts to UTC and
    // shifts positive-UTC users (e.g. AEST) into the previous day.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  return (
    <GlassPanel className="!p-4 border-red-500/30 bg-red-500/[0.06]" data-testid="overload-alerts">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-300 shrink-0">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-heading text-white text-lg tracking-tight">
              {alerts.length === 1 ? "1 installer overloaded" : `${alerts.length} installers overloaded`} next week
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-200/70">
              {fmtYmd(nextWeek.start)} → {fmtYmd(nextWeek.end)}
            </div>
          </div>
          <p className="text-xs text-slate-300/80 mt-0.5">
            Time to rebalance the calendar before Monday.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {alerts.map(({ user: u, hours, cap, pct }) => (
              <div
                key={u.id}
                data-testid={`overload-alert-${u.id}`}
                className="flex items-center gap-3 rounded-xl bg-black/25 border border-white/10 px-3 py-2"
              >
                <span
                  className="w-3 h-3 rounded-sm border border-white/20 shrink-0"
                  style={{ background: u.color || "rgba(148,163,184,0.6)" }}
                  aria-hidden
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{u.name || u.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    {Number.isInteger(hours) ? hours : hours.toFixed(1)}h of {cap}h cap
                  </div>
                </div>
                <div className={`tabular-nums text-sm font-heading ${pct >= 100 ? "text-red-300" : "text-amber-300"}`}>
                  {Math.round(pct)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}
